import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { COLOR } from "../../../utils/colors";
import { GridMenuIcon } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { ApartmentOutlined, EditRoadOutlined, HomeOutlined, KeyboardArrowDown, LocationOnOutlined, MapOutlined, SettingsSuggestOutlined, SignpostOutlined, SmartToyOutlined, SyncAltOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { shadow } from "../../../utils/styles";
import { useIsExpanded, useSharedCurrentStep, useSharedIsExpanded } from "../_functions";
import { parseAsInteger, useQueryState } from "nuqs";

const menus = [
  {
    title: 'Buildings',
    value: 'buildings',
    icon: <ApartmentOutlined />
  }, {
    title: 'Layout Maps',
    value: 'layout-maps',
    icon: <MapOutlined />
  }, {
    title: 'Waypoints',
    value: 'locations',
    icon: <LocationOnOutlined />
  }, {
    title: 'Robots',
    value: 'robots',
    icon: <SmartToyOutlined />
  }, {
    title: 'Robot Maps',
    value: 'robot-maps',
    icon: <SignpostOutlined />
  }, {
    title: 'Bind Robot Map GIS Points',
    value: 'bind-layout-maps',
    icon: <SyncAltOutlined />
  }, {
    title: 'Assign Robots Maps to Robots',
    value: 'assign-robot',
    icon: <EditRoadOutlined />
  }
]

const quickSetupMenus = [
  {
    title: 'Building Set Up',
    value: 'create-building',
  }, {
    title: 'Create Floor Layout Map',
    value: 'create-floor-layout',
  }, {
    title: 'Add Waypoints',
    value: 'add-locations',
  }, {
    title: 'Add Robots',
    value: 'add-robots',
  }, {
    title: 'Add Robot Map',
    value: 'add-robot-map',
  }, {
    title: 'Bind Robot Map GIS Points',
    value: 'bind-layout-map-robot',
  }, {
    title: 'Assign Robot Maps to Robots',
    value: 'calibrate-robot',
  }
]

export default function ConfigSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useQueryState("step", parseAsInteger.withDefault(1));
  const activeStep = step ? step - 1 : 0;
  const pathName = location.pathname || '';
  const { isExpanded, setIsExpanded } = useSharedIsExpanded();
  const isInQuickSetup = pathName.includes('/config/quick-step');
  const [isWantToLeaveQuickSetup, setIsWantToLeaveQuickSetup] = useState(false);
  const [wantToRedirectUrl, setWantToRedirectUrl] = useState<string | undefined>(undefined);
  const [quickSetupExpand, setQuickSetupExpand] = useState(isInQuickSetup);
  const onBackRobohub = () => {
    navigate('/')
  }

  useEffect(() => {
    if (!isInQuickSetup) {
      if (quickSetupExpand) {
        setQuickSetupExpand(false);
      }
      return
    }

    setQuickSetupExpand(true)
  }, [isInQuickSetup]);

  const currentQuickSetupStep = isInQuickSetup ? quickSetupMenus[activeStep] : undefined;

  const handleStep = (value: number) => {
    if (value + 1 > step) {
      return
    }

    setStep(value + 1)
  }

  const onHandleQuickSetup = () => {
    if (!isInQuickSetup) {
      navigate('/config/quick-step');
      return
    }

    setQuickSetupExpand(!quickSetupExpand);
  }

  const isCurrentPath = (menuValue: string) => {
    return pathName.includes('/config/' + menuValue);
  }

  const handleClickMenu = (menuValue: string) => {
    // if (isCurrentPath(menuValue)) {
    //   return;
    // }
    const targetUrl = '/config/' + menuValue
    if (isInQuickSetup) {
      setIsWantToLeaveQuickSetup(true);
      setWantToRedirectUrl(targetUrl)
      return
    }
    navigate(targetUrl);
  }

  const onCancelToLeaveQuickSetup = () => {
    setIsWantToLeaveQuickSetup(false);
    setWantToRedirectUrl(undefined);
  }

  const handleLeaveQuickSetup = () => {
    setIsWantToLeaveQuickSetup(false);
    if (!wantToRedirectUrl) {
      return;
    }
    navigate(wantToRedirectUrl);
    setWantToRedirectUrl(undefined);
  }

  return (
    <Box sx={{
      width: isExpanded ? '320px' : '72px',
      maxWidth: isExpanded ? '320px' : '72px',
      transition: 'all 0.3s',
      flex: 'none',
      display: 'flex',
      flexDirection: 'column',
      // maxWidth: '320px',
      height: '100vh',
      backgroundColor: COLOR.white,
      justifyContent: 'space-between',
      overflowX: 'hidden',
      ...shadow,
      '&:hover': {},
      userSelect: 'none',
      zIndex: '30'
    }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: COLOR.neutral[900],
          display: 'flex',
          padding: '24px',
          alignItems: 'center',
          gap: isExpanded ? '24px' : '0px',
          justifyContent: isExpanded ? 'flex-start' : 'center',
        }}>
          <Box
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'center',
              flex: 'none'
            }}>
            <GridMenuIcon />
          </Box>
          {isExpanded && (
            <Typography
              sx={{
                fontSize: '24px',
                fontWeight: 600,
                color: COLOR.neutral[900]
              }}>
              RoboHub Config
            </Typography>
          )}
        </Box>

        {/* {isExpanded && ( */}
          <Box sx={{ display: 'flex', mt: '16px', flexDirection: 'column' }}>
            <Box>
              <Box
                onClick={() => onHandleQuickSetup()}
                sx={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.3s',
                  paddingY: '14px',
                  paddingX: '24px',
                  cursor: 'pointer',
                  color: isInQuickSetup ? COLOR.accent.blue : COLOR.neutral[900],
                  backgroundColor: isInQuickSetup ? '#E5EFFF' : COLOR.white,
                  '&:hover': {
                    backgroundColor: '#F2F7FF',
                    color: COLOR.accent.blue,
                  },
                }}>
                <Box
                  key={'quick-setup'}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flex: 'auto',
                  }}>
                  {!isInQuickSetup && (
                    <SettingsSuggestOutlined />
                  )}
                  {isInQuickSetup && (
                    <Typography sx={{ fontSize: '12px', flex: 'none' }}>
                      {activeStep + 1} / {quickSetupMenus.length}
                    </Typography>
                  )}
                  {isExpanded && (
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column' }}
                    >
                      {isInQuickSetup && (
                        <Typography sx={{
                          fontSize: '10px',
                          fontWeight: 400
                        }}>
                          Quick Set Up
                        </Typography>
                      )}
                      <Typography sx={{
                        fontSize: '16px',
                        fontWeight: isInQuickSetup ? 600 : 500
                      }}>
                        {isInQuickSetup ? currentQuickSetupStep?.title : 'Quick Set Up'}
                      </Typography>
                    </Box>
                  )}
                </Box>
                {isInQuickSetup && isExpanded && (
                  <Box sx={{ transform: quickSetupExpand ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'all 0.3s' }}>
                    <KeyboardArrowDown />
                  </Box>
                )}
              </Box>
              {isExpanded && quickSetupExpand && (
                <Box sx={{ paddingX: '24px', paddingY: '14px' }}>
                  <Stepper activeStep={activeStep} orientation="vertical">
                    {quickSetupMenus.map((step, index) => (
                      <Step key={index} sx={{ cursor: 'pointer' }} onClick={() => handleStep(index)}>
                        <StepLabel>
                          {step.title}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
              )}
            </Box>
            {menus.map((menu, index) => (
              <Box
                key={index}
                onClick={() => handleClickMenu(menu.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  paddingY: '14px',
                  paddingX: '24px',
                  cursor: 'pointer',
                  color: isCurrentPath(menu.value) ? COLOR.accent.blue : COLOR.neutral[900],
                  backgroundColor: isCurrentPath(menu.value) ? '#E5EFFF' : COLOR.white,
                  '&:hover': {
                    backgroundColor: '#F2F7FF',
                    color: COLOR.accent.blue,
                  },
                  transition: 'all 0.3s',
                }}>
                {menu.icon}
                {isExpanded && (
                  <Typography sx={{
                    fontSize: '16px',
                    fontWeight: isCurrentPath(menu.value) ? 600 : 500
                  }}>
                    {menu.title}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        {/* )} */}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          paddingX: isExpanded ? '24px' : '24px',
          paddingY: '16px',
          cursor: 'pointer',
          color: COLOR.neutral[900],
          gap: '12px',
          '&:hover': {
            backgroundColor: '#F2F7FF',
            color: COLOR.accent.blue,
          },
          transition: 'all 0.3s',
        }}
        onClick={() => onBackRobohub()}
      >
        <HomeOutlined />
        {isExpanded && (
          <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
            Back to Robohub
          </Typography>
        )}
      </Box>

      <Dialog
        open={isWantToLeaveQuickSetup}
        onClose={() => onCancelToLeaveQuickSetup()}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Are you sure ?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You are in Quick Setup Page, Are you sure want to leave this page ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onCancelToLeaveQuickSetup()} autoFocus>No, Cancel</Button>
          <Button onClick={() => handleLeaveQuickSetup()}>
            Yes, Sure
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}